import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { fmtDate, euro } from '../../utils/formatUtils.js';
import { sendAgreementToClient } from '../../utils/agreementUtils.js';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useActionsCount } from '../../contexts/ActionsCountContext';

export default function ActiesPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { setCount } = useActionsCount();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [afgewezenTokens, setAfgewezenTokens] = useState([]);
  const [verlopenTokens, setVerlopenTokens] = useState([]);
  const [prospecten, setProspecten] = useState([]);
  const [incasso, setIncasso] = useState([]);
  const [verzondenProspectTokens, setVerzondenProspectTokens] = useState({});

  const [sendingTokenId, setSendingTokenId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);

  const [confirmProspect, setConfirmProspect] = useState(null);
  const [confirmSending, setConfirmSending] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const now = new Date().toISOString();

      const [afgewezenRes, verlopenRes, prospectenRes, incassoRes, signedRes, dismissedRes, openstaandProspectTokensRes] = await Promise.all([
        supabase
          .from('agreement_tokens')
          .select('id, token, created_at, rejected_at, rejection_reason, client:profiles!agreement_tokens_client_id_fkey(id, full_name, email), membership_type:membership_types(name)')
          .not('rejected_at', 'is', null)
          .not('rejected_by', 'is', null)
          .order('rejected_at', { ascending: false })
          .limit(50),

        supabase
          .from('agreement_tokens')
          .select('id, token, created_at, expires_at, client:profiles!agreement_tokens_client_id_fkey(id, full_name, email), membership_type:membership_types(name)')
          .is('used_at', null)
          .is('rejected_at', null)
          .lt('expires_at', now)
          .order('expires_at', { ascending: false })
          .limit(50),

        supabase
          .from('profiles')
          .select('id, full_name, email, created_at')
          .eq('client_status', 'prospect')
          .order('created_at', { ascending: false })
          .limit(50),

        supabase
          .from('invoices')
          .select('id, invoice_number, total_amount, client_id, client:profiles!invoices_client_id_fkey(id, full_name)')
          .eq('status', 'collections')
          .order('created_at', { ascending: false })
          .limit(50),

        // Automatisch afsluiten: clients die al een overeenkomst ondertekend hebben
        supabase.from('signed_agreements').select('client_id'),

        // Handmatig afgehandelde acties voor deze coach
        supabase.from('dismissed_actions')
          .select('reference_id, action_type')
          .eq('coach_id', user.id),

        // Openstaande (nog niet verlopen/afgewezen/gebruikte) tokens: toont "al actie ondernomen" bij prospects
        supabase.from('agreement_tokens')
          .select('client_id, created_at')
          .is('used_at', null)
          .is('rejected_at', null)
          .gt('expires_at', now)
          .order('created_at', { ascending: false }),
      ]);

      if (afgewezenRes.error) throw afgewezenRes.error;
      if (verlopenRes.error) throw verlopenRes.error;
      if (prospectenRes.error) throw prospectenRes.error;
      if (incassoRes.error) throw incassoRes.error;
      if (openstaandProspectTokensRes.error) throw openstaandProspectTokensRes.error;

      // Bouw een set van client_ids die al ondertekend hebben (automatisch verbergen)
      const signedClientIds = new Set((signedRes.data || []).map(s => s.client_id));

      // Bouw een set van handmatig afgehandelde items: "action_type:reference_id"
      const dismissedSet = new Set((dismissedRes.data || []).map(d => `${d.action_type}:${d.reference_id}`));

      // Afgewezen tokens: verberg als client al ondertekend heeft OF handmatig afgehandeld
      const afgewezen = (afgewezenRes.data || []).filter(tk =>
        tk.rejected_at != null &&
        !signedClientIds.has(tk.client?.id) &&
        !dismissedSet.has(`rejected_token:${tk.id}`)
      );

      // Verlopen tokens: verberg als client al ondertekend heeft OF handmatig afgehandeld
      const verlopen = (verlopenRes.data || []).filter(tk =>
        !signedClientIds.has(tk.client?.id) &&
        !dismissedSet.has(`expired_token:${tk.id}`)
      );

      // Prospects: verberg handmatig afgehandelde
      const prospects = (prospectenRes.data || []).filter(p =>
        !dismissedSet.has(`prospect:${p.id}`)
      );

      // Incasso: verberg handmatig afgehandelde
      const incassoItems = (incassoRes.data || []).filter(inv =>
        !dismissedSet.has(`incasso:${inv.id}`)
      );

      // Meest recente openstaande token per client (voor "al verstuurd, wacht op ondertekening")
      const verzondenMap = {};
      (openstaandProspectTokensRes.data || []).forEach(tk => {
        if (!verzondenMap[tk.client_id]) verzondenMap[tk.client_id] = tk.created_at;
      });

      setAfgewezenTokens(afgewezen);
      setVerlopenTokens(verlopen);
      setProspecten(prospects);
      setIncasso(incassoItems);
      setVerzondenProspectTokens(verzondenMap);

      const totaal = afgewezen.length + verlopen.length + prospects.length + incassoItems.length;
      setCount(totaal);
    } catch (err) {
      setError(t('acties.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleNieuwToken(clientRow, suggestedTypeId = null) {
    const tokenId = clientRow.id || clientRow.client_id;
    setSendingTokenId(tokenId);
    try {
      await sendAgreementToClient(
        tokenId,
        user.id,
        suggestedTypeId,
        clientRow.email,
        clientRow.full_name
      );
      showSuccess(t('agreement.verstuurdSuccess'));
      await load();
    } catch (err) {
      showError(t('agreement.verstuurdFout') + err.message);
    } finally {
      setSendingTokenId(null);
    }
  }

  async function handleConfirmVersturen() {
    const prospect = confirmProspect;
    if (!prospect) return;
    setConfirmSending(true);
    setConfirmError('');
    try {
      await sendAgreementToClient(
        prospect.id,
        user.id,
        null,
        prospect.email,
        prospect.full_name
      );
      showSuccess(t('agreement.verstuurdSuccess'));
      setConfirmProspect(null);
      await load();
    } catch (err) {
      setConfirmError(t('agreement.verstuurdFout') + err.message);
    } finally {
      setConfirmSending(false);
    }
  }

  async function handleDismiss(actionType, referenceId) {
    const key = `${actionType}:${referenceId}`;
    setDismissingId(key);
    try {
      const { error: err } = await supabase.from('dismissed_actions').insert({
        coach_id: user.id,
        action_type: actionType,
        reference_id: referenceId,
      });
      if (err) throw err;

      // Verwijder direct uit de juiste lijst (geen volledige refresh)
      if (actionType === 'rejected_token') {
        setAfgewezenTokens(prev => prev.filter(tk => tk.id !== referenceId));
      } else if (actionType === 'expired_token') {
        setVerlopenTokens(prev => prev.filter(tk => tk.id !== referenceId));
      } else if (actionType === 'prospect') {
        setProspecten(prev => prev.filter(p => p.id !== referenceId));
      } else if (actionType === 'incasso') {
        setIncasso(prev => prev.filter(inv => inv.id !== referenceId));
      }

      setCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      showError(t('acties.foutAfhandelen') + err.message);
    } finally {
      setDismissingId(null);
    }
  }

  const totaalActies = afgewezenTokens.length + verlopenTokens.length + prospecten.length + incasso.length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('acties.titel')}</h1>
          {!loading && (
            <p className="page-subtitle" style={{ color: totaalActies > 0 ? '#dc2626' : 'var(--color-text-muted)' }}>
              {totaalActies === 0 ? t('acties.geenActies') : `${totaalActies} item${totaalActies !== 1 ? 's' : ''} vereisen aandacht`}
            </p>
          )}
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <>
          {/* Afgewezen overeenkomsten */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              ❌ {t('acties.afgewezenOvereenkomsten')}
              {afgewezenTokens.length > 0 && (
                <span style={{ marginLeft: 8, fontSize: 12, background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 99 }}>
                  {afgewezenTokens.length}
                </span>
              )}
            </h2>
            {afgewezenTokens.length === 0 ? (
              <p className="text-muted">{t('acties.geenItems')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>{t('agreement.datumVerzonden')}</th>
                      <th>{t('acties.afgewezenDoor')}</th>
                      <th style={{ textAlign: 'right' }}>Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {afgewezenTokens.map(tk => (
                      <tr key={tk.id}>
                        <td>
                          <Link to={`/coach/clients/${tk.client?.id}`} className="font-medium">
                            {tk.client?.full_name || '—'}
                          </Link>
                          <div className="text-muted" style={{ fontSize: 12 }}>{tk.client?.email}</div>
                        </td>
                        <td className="text-muted">{fmtDate(tk.created_at)}</td>
                        <td>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {fmtDate(tk.rejected_at)}
                            {tk.rejection_reason && (
                              <div style={{ color: '#dc2626' }}>{t('acties.redenAfwijzing')}{tk.rejection_reason}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleNieuwToken(tk.client)}
                              disabled={sendingTokenId === tk.client?.id}
                            >
                              {sendingTokenId === tk.client?.id ? '…' : t('acties.stuurOpnieuw')}
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleDismiss('rejected_token', tk.id)}
                              disabled={dismissingId === `rejected_token:${tk.id}`}
                              title={t('acties.afhandelen')}
                            >
                              {dismissingId === `rejected_token:${tk.id}` ? '…' : '✓'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Verlopen overeenkomsten */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              ⏰ {t('acties.verlopenOvereenkomsten')}
              {verlopenTokens.length > 0 && (
                <span style={{ marginLeft: 8, fontSize: 12, background: '#fff7ed', color: '#ea580c', padding: '2px 8px', borderRadius: 99 }}>
                  {verlopenTokens.length}
                </span>
              )}
            </h2>
            {verlopenTokens.length === 0 ? (
              <p className="text-muted">{t('acties.geenItems')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>{t('agreement.datumVerzonden')}</th>
                      <th>{t('acties.verlopenOp')}</th>
                      <th style={{ textAlign: 'right' }}>Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verlopenTokens.map(tk => (
                      <tr key={tk.id}>
                        <td>
                          <Link to={`/coach/clients/${tk.client?.id}`} className="font-medium">
                            {tk.client?.full_name || '—'}
                          </Link>
                          <div className="text-muted" style={{ fontSize: 12 }}>{tk.client?.email}</div>
                        </td>
                        <td className="text-muted">{fmtDate(tk.created_at)}</td>
                        <td className="text-muted">{fmtDate(tk.expires_at)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleNieuwToken(tk.client)}
                              disabled={sendingTokenId === tk.client?.id}
                            >
                              {sendingTokenId === tk.client?.id ? '…' : t('acties.stuurOpnieuw')}
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleDismiss('expired_token', tk.id)}
                              disabled={dismissingId === `expired_token:${tk.id}`}
                              title={t('acties.afhandelen')}
                            >
                              {dismissingId === `expired_token:${tk.id}` ? '…' : '✓'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Prospects zonder actie */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              👤 {t('acties.prospectenZonderActie')}
              {prospecten.length > 0 && (
                <span style={{ marginLeft: 8, fontSize: 12, background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 99 }}>
                  {prospecten.length}
                </span>
              )}
            </h2>
            {prospecten.length === 0 ? (
              <p className="text-muted">{t('acties.geenItems')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>E-mail</th>
                      <th>{t('acties.aangemeldOp')}</th>
                      <th style={{ textAlign: 'right' }}>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prospecten.map(p => (
                      <tr key={p.id}>
                        <td className="font-medium">
                          {p.full_name}
                          {verzondenProspectTokens[p.id] && (
                            <div style={{ fontSize: 12, fontWeight: 400, color: '#1d4ed8', marginTop: 2 }}>
                              {t('acties.overeenkomstVerstuurdOp')}{fmtDate(verzondenProspectTokens[p.id])}{t('acties.wachtOpOndertekening')}
                            </div>
                          )}
                        </td>
                        <td className="text-muted">{p.email}</td>
                        <td className="text-muted">{fmtDate(p.created_at)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Link to={`/coach/clients/${p.id}`} className="btn btn-ghost btn-sm">
                              {t('acties.clientBekijken')}
                            </Link>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => { setConfirmError(''); setConfirmProspect(p); }}
                              disabled={sendingTokenId === p.id}
                            >
                              {t('acties.actieUitvoeren')}
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleDismiss('prospect', p.id)}
                              disabled={dismissingId === `prospect:${p.id}`}
                              title={t('acties.afhandelen')}
                            >
                              {dismissingId === `prospect:${p.id}` ? '…' : '✓'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Openstaande incasso */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
              ⚠️ {t('acties.openstaandeIncasso')}
              {incasso.length > 0 && (
                <span style={{ marginLeft: 8, fontSize: 12, background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 99 }}>
                  {incasso.length}
                </span>
              )}
            </h2>
            {incasso.length === 0 ? (
              <p className="text-muted">{t('acties.geenItems')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Factuurnummer</th>
                      <th>{t('acties.openstaandBedrag')}</th>
                      <th style={{ textAlign: 'right' }}>Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incasso.map(inv => (
                      <tr key={inv.id}>
                        <td className="font-medium">{inv.client?.full_name || '—'}</td>
                        <td className="text-muted">{inv.invoice_number}</td>
                        <td style={{ fontWeight: 600, color: '#dc2626' }}>{euro(inv.total_amount)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Link to={`/coach/clients/${inv.client_id}`} className="btn btn-ghost btn-sm">
                              {t('acties.clientBekijken')}
                            </Link>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleDismiss('incasso', inv.id)}
                              disabled={dismissingId === `incasso:${inv.id}`}
                              title={t('acties.afhandelen')}
                            >
                              {dismissingId === `incasso:${inv.id}` ? '…' : '✓'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {confirmProspect && (
        <div className="modal-overlay" onClick={() => { if (!confirmSending) setConfirmProspect(null); }}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('acties.bevestigVersturenTitel')}</h2>
              <button className="modal-close" onClick={() => setConfirmProspect(null)} disabled={confirmSending}>✕</button>
            </div>

            <p style={{ marginBottom: 8 }}>
              <span className="text-muted">{t('acties.bevestigVersturenNaam')}</span>
              <strong>{confirmProspect.full_name || '—'}</strong>
            </p>
            <p style={{ marginBottom: 8 }}>
              <span className="text-muted">{t('acties.bevestigVersturenTemplate')}</span>
              {t('acties.algemeneOvereenkomst')}
            </p>
            <p style={{ marginBottom: 8 }}>
              <span className="text-muted">{t('acties.bevestigVersturenEmail')}</span>
              <strong>{confirmProspect.email || '—'}</strong>
            </p>
            <p style={{ marginBottom: 16 }}>
              <Link to={`/coach/overeenkomst/opstellen/${confirmProspect.id}`} style={{ fontSize: 13 }}>
                {t('acties.ofZelfSamenstellen')}
              </Link>
            </p>

            {(!confirmProspect.email || !confirmProspect.email.includes('@')) && (
              <div className="error-msg" style={{ marginBottom: 16 }}>{t('acties.emailOntbreektOfOngeldig')}</div>
            )}

            {confirmError && (
              <div className="error-msg" style={{ marginBottom: 16 }}>{confirmError}</div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmProspect(null)} disabled={confirmSending}>
                {t('acties.annuleren')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmVersturen}
                disabled={confirmSending || !confirmProspect.email || !confirmProspect.email.includes('@')}
              >
                {confirmSending ? t('agreement.versturenBezig') : t('agreement.versturen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
